/* Beds & Bedding core helpers. No dependencies. Loaded once, deferred. */
(function () {
  'use strict';

  var cfgEl = document.getElementById('bb-config');
  var config = {};
  try { config = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (e) { config = {}; }

  var tracked = new Set();

  function track(event, payload, dedupeKey) {
    if (dedupeKey) {
      var key = event + ':' + dedupeKey;
      if (tracked.has(key)) return;
      tracked.add(key);
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  }

  /* JSON fetch with the platform timeout (theme setting `store_platform_timeout`, one policy for every platform call).
     `options.json` turns it into a JSON POST (body + Content-Type); no credentials, no custom auth headers, nothing logged.
     Errors carry `status` (HTTP) and `body` (parsed error envelope) for the caller; a timeout aborts → `AbortError`. */
  function fetchJSON(url, options) {
    options = options || {};
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeout = options.timeout || (config.platform && config.platform.timeoutMs) || 5000;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeout) : null;
    var init = Object.assign({ headers: { Accept: 'application/json' } }, options.init || {});
    if (options.json !== undefined) {
      init.method = init.method || 'POST';
      init.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
      init.body = JSON.stringify(options.json);
    }
    if (controller) init.signal = controller.signal;
    return fetch(url, init).then(function (res) {
      if (timer) clearTimeout(timer);
      if (!res.ok) {
        var err = new Error('HTTP ' + res.status);
        err.status = res.status;
        return res.json().then(function (body) { err.body = body; throw err; }, function () { throw err; });
      }
      return res.json();
    }, function (e) { if (timer) clearTimeout(timer); throw e; });
  }

  function platformUrl(path) {
    var base = config.platform && config.platform.baseUrl;
    if (!base) return null;
    return base.replace(/\/+$/, '') + path;
  }

  /* Canonical page context (snippets/page-context.liquid, `#bb-page-context`): the one source of product/brand/store
     context for platform calls — product identity is the Shopify product GID (`product_gid`), never a handle. */
  function pageContext() {
    var el = document.getElementById('bb-page-context');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  /* Open-now projection of a public store DTO (P9): `openNow` true → open, false → closed, null/absent → unknown (never
     shown as closed). Text adds "closes at" from `today.windows` when open; when closed with windows no "opens at" is
     claimed (the projection does not say whether that window is still ahead); closed without windows → closed today. */
  function storeOpenState(store) {
    var open = store ? store.openNow : undefined;
    if (open !== true && open !== false) return { state: null, text: '' };
    var windows = store.today && Array.isArray(store.today.windows) ? store.today.windows.filter(function (w) { return w && typeof w.openTime === 'string' && typeof w.closeTime === 'string'; }) : [];
    var text = open ? t('openNow') : t('closedNow');
    if (open && windows.length) text += ' · ' + t('closesAt', { time: windows[windows.length - 1].closeTime });
    else if (!open && !windows.length) text = t('closedToday');
    return { state: open ? 'open' : 'closed', text: text };
  }

  function formatMoney(cents) {
    var format = config.moneyFormat || '€{{amount_with_comma_separator}}';
    var value = (cents / 100);
    var withComma = value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var withDot = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, withDot);
  }

  function t(key, vars) {
    var s = (config.strings && config.strings[key]) || key;
    if (vars) Object.keys(vars).forEach(function (k) { s = s.split('[' + k + ']').join(vars[k]); });
    return s;
  }

  var toastTimer;
  function toast(html, opts) {
    var el = document.getElementById('bb-toast');
    if (!el) return;
    el.innerHTML = '';
    if (typeof html === 'string') { el.textContent = html; } else { el.appendChild(html); }
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, (opts && opts.duration) || 4000);
  }

  /* Native <dialog> helpers: scroll lock, backdrop click and focus return per dialog instance. */
  var dialogFocus = new WeakMap();
  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialogFocus.set(dialog, document.activeElement);
    dialog.showModal();
    document.body.classList.add('bb-scroll-lock');
    var first = dialog.querySelector('[autofocus]') ||
      dialog.querySelector('[data-bb-dialog-close]') ||
      dialog.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (first && typeof first.focus === 'function') first.focus();
    dialog.dispatchEvent(new CustomEvent('bb:dialog:open'));
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }
  document.addEventListener('close', function (e) {
    if (!(e.target instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('bb-scroll-lock');
    var returnFocus = dialogFocus.get(e.target);
    dialogFocus.delete(e.target);
    if (returnFocus && returnFocus.isConnected && typeof returnFocus.focus === 'function') returnFocus.focus();
    e.target.dispatchEvent(new CustomEvent('bb:dialog:close'));
  }, true);
  document.addEventListener('click', function (e) {
    var dialog = e.target;
    if (dialog instanceof HTMLDialogElement && dialog.open && dialog.hasAttribute('data-bb-backdrop-close')) {
      var r = dialog.getBoundingClientRect();
      var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) closeDialog(dialog);
    }
    var closer = e.target.closest('[data-bb-dialog-close]');
    if (closer) { var d = closer.closest('dialog'); if (d) closeDialog(d); }
    var opener = e.target.closest('[data-bb-dialog-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-bb-dialog-open'));
      if (target) { e.preventDefault(); openDialog(target); }
    }
  });

  /* Generic analytics hooks that need no component script. */
  document.addEventListener('click', function (e) {
    /* CRO hooks: one taxonomy — event bb_product_interaction with interaction (= data-bb-track value) and placement (data-bb-placement). Details elements track on toggle (bb-product.js). */
    var hook = e.target.closest('[data-bb-track]');
    if (hook && hook.tagName !== 'DETAILS' && hook.tagName !== 'SUMMARY') {
      var pctx = document.querySelector('.bb-product[data-product-id]');
      track('bb_product_interaction', {
        interaction: hook.getAttribute('data-bb-track'),
        placement: hook.getAttribute('data-bb-placement') || hook.getAttribute('data-placement') || null,
        product_id: pctx ? pctx.getAttribute('data-product-id') : null,
        store_slug: hook.getAttribute('data-store-slug') || hook.getAttribute('data-bb-store-call') || hook.getAttribute('data-bb-store-route') || null,
        label: (hook.textContent || '').trim().slice(0, 60)
      });
    }
    /* Store events follow the shared snake_case family (docs/store-platform-integration.md §7). */
    var call = e.target.closest('[data-bb-store-call]');
    if (call) track('store_call_click', { store_slug: call.getAttribute('data-bb-store-call') });
    var route = e.target.closest('[data-bb-store-route]');
    if (route) track('store_route_click', { store_slug: route.getAttribute('data-bb-store-route') });
    var storeSelect = e.target.closest('[data-bb-store-select]');
    if (storeSelect) track('store_selected', { store_slug: storeSelect.getAttribute('data-bb-store-select'), via: 'store_page' });
    var campaign = e.target.closest('[data-bb-campaign-click]');
    if (campaign) {
      var wrap = campaign.closest('[data-bb-campaign]');
      track('campaign_click', {
        campaign_id: wrap ? wrap.getAttribute('data-bb-campaign') : null,
        campaign_level: wrap ? wrap.getAttribute('data-bb-campaign-level') : null,
        brand: wrap ? wrap.getAttribute('data-bb-brand') : null,
        target: campaign.getAttribute('data-bb-campaign-click')
      });
    }
    var select = e.target.closest('[data-bb-select-item]');
    if (select) {
      var card = select.closest('[data-product-id]');
      if (card) track('select_item', {
        item_id: card.getAttribute('data-product-id'),
        item_name: select.textContent.trim(),
        item_list_name: card.getAttribute('data-bb-list') || null
      });
    }
  });

  /* campaign_view when a promotion component becomes visible (once per campaign per page). */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        track('campaign_view', {
          campaign_id: el.getAttribute('data-bb-campaign'),
          campaign_level: el.getAttribute('data-bb-campaign-level'),
          brand: el.getAttribute('data-bb-brand')
        }, el.getAttribute('data-bb-campaign'));
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    var observeCampaigns = function () {
      document.querySelectorAll('[data-bb-campaign]:not([data-bb-observed])').forEach(function (el) {
        el.setAttribute('data-bb-observed', '');
        io.observe(el);
      });
    };
    observeCampaigns();
    document.addEventListener('shopify:section:load', observeCampaigns);
  }

  /* Booking funnel and conversion events (appointment_* incl. appointment_confirmed) are emitted by the platform runtime only
     (ICR-003). The theme derives no analytics from bb-booking:confirmed. */

  window.BB = { config: config, track: track, fetchJSON: fetchJSON, platformUrl: platformUrl, pageContext: pageContext, storeOpenState: storeOpenState, formatMoney: formatMoney, t: t, toast: toast, openDialog: openDialog, closeDialog: closeDialog };
  document.dispatchEvent(new CustomEvent('bb:ready'));
})();
